/**
 * Capitalize the first letter of each word in a string.
 *
 * @param input - The input string to capitalize.
 * @param onlyFirst - If true, only the first letter of the first word is capitalized.
 * @return The input string with the first letter of each word capitalized.
 */
export function capitalize<T extends string | undefined>(input: T, onlyFirst = false): T {
  return input?.split(/\s/).map((w, i) => {
    if (onlyFirst && i > 0) return w
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  }).join(" ") as T
}

/**
 * Convert a string to a specific casing format.
 *
 * Supported casings:
 * - "lower": converts to lowercase
 * - "upper": converts to UPPERCASE
 * - "camel": converts to camelCase
 * - "pascal": converts to PascalCase
 * - "snake": converts to snake_case
 * - "kebab": converts to kebab-case
 *
 * @param input - The input string to convert.
 * @param casing - The casing format to convert the string to.
 * @return The converted string in the specified casing format.
 */
export function convertToCase<T extends string | undefined>(input: T, casing: "lower" | "upper" | "camel" | "pascal" | "snake" | "kebab"): T {
  if (!input) return input

  switch (casing) {
    case "lower":
      return input.toLowerCase() as T
    case "upper":
      return input.toUpperCase() as T
    case "camel":
      return (input
        .match(/[a-z0-9]+/gi)
        ?.map((w, i) =>
          i === 0
            ? w[0]?.toLowerCase() + w.slice(1)
            : w[0]?.toUpperCase() + w.slice(1),
        )
        .join("") ?? undefined) as T
    case "pascal":
      return (input
        .match(/[a-z0-9]+/gi)
        ?.map(w => w[0]?.toUpperCase() + w.slice(1))
        .join("") ?? undefined) as T
    case "snake":
      return input.replace(/\s+/g, "_").toLowerCase() as T
    case "kebab":
      return input.replace(/\s+/g, "-").toLowerCase() as T
    default:
      return input
  }
}

export function onlyChars<T extends string | undefined>(input: T): T {
  return input?.replace(/[^\w\s]/g, "") as T
}
